import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { GlobalContext } from "../../common/Context";
import * as constants from "../../../constants";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { useTranslation } from "react-i18next";
import { green, purple, text } from "../../../theme/colours";
import { emailId, passwordId, signErrorId } from "../__fixtures__/constants";

const SignForm = ({ container, validInvite, willLink }) => {
  const { t } = useTranslation("Account");
  const {
    mobileView,
    tabletView,
    desktopView,
    signError,
    setSignError,
    setSignLoad,
    firebaseState,
  } = useContext(GlobalContext);
  const location = useLocation();
  const inviteId = location.state?.inviteId;
  const linkingOptions = {};
  const startIconStyle = {
    m: "1rem",
    ml: "0.25rem",
  };
  const adornmentStyle = {
    width: mobileView || tabletView ? "1rem" : "1.25rem",
    color: text.primary,
  };
  const buttonContainer = {
    ...container,
    width: desktopView ? "95%" : "100%",
    mt: mobileView || tabletView ? "1.25rem" : "0.75rem",
  };
  const button = {
    mb: mobileView ? ".5rem" : "0.75rem",
    width: "100%",
    padding: mobileView || tabletView ? "0.25rem" : "0.6rem",
    borderRadius: "0.5rem",
    fontWeight: 700,
    fontSize: mobileView ? ".75rem" : "1.15rem",
    height: tabletView ? "3.5rem" : "2.5rem",
  };
  const logButton = {
    ...button,
    background: `linear-gradient(90deg, ${green[1]}, ${purple[1]})`,
    ":hover": {
      background: `linear-gradient(90deg, ${green[2]}, ${purple[2]})`,
      opacity: 0.9,
    },
  };
  const googleButton = {
    ...button,
    color: text.primary,
    border: `0.2rem solid ${text.primary}`,
  };
  const passButton = {
    width: desktopView ? "12rem" : "100%",
    fontSize: mobileView ? ".75rem" : "0.85rem",
    fontWeight: 700,
    textTransform: "none",
  };
  const inputStyle = {
    width: mobileView || tabletView ? "100%" : "95%",
    backgroundColor: "#fff",
    borderRadius: "2rem",
    marginBottom: !mobileView && !tabletView ? "1.25rem" : "0.5rem",
    boxShadow: "0 2px 3px #EAEAEA",
    height: desktopView ? "auto" : tabletView ? "4rem" : "3rem",
  };
  const fontStyle = { fontSize: "1rem" };
  const tinyBtnStyle = {
    background: "transparent",
    width: "100%",
    textDecoration: "underline",
    color: text.primary,
    fontSize: "0.65rem",
    marginBottom: "0.25rem",
  };
  const classStyles = `
  .MuiOutlinedInput-root {
    border-radius: 2rem;
    padding: 0;
    border:none;
    color: ${text.primary};
  }

  .MuiInputBase-root {
    font-size: ${!tabletView ? "0.75rem" : "1.25rem"};
    color: ${text.primary};
  }

  .MuiOutlinedInput-input {
    color: ${text.primary};
    padding: ${
      !mobileView && !tabletView
        ? "24px 14px 24px 80px;"
        : "1rem 14px 1rem 80px;"
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputAdornment-root {
    position: absolute;
    right: 12px;
  }

  .MuiInputAdornment-positionStart {
    left: 28px;
  }
  
  .MuiButton-outlinedInfo {
    &:hover:{
      border: 0.2rem solid ${text.primary};
    }
  }
  .MuiButton-contained {
    &:hover:{
        background: linear-gradient(90deg, ${green[2]}, ${purple[2]});
        opacity: 0.9;
    }
  }
`;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [textType, setTextType] = useState("password");

  const onSubmit = async ({ email, password }) => {
    if (willLink && inviteId && !validInvite) {
      setSignError(t("waitVerifyInvite"));
      return;
    }
    try {
      setSignError("");
      setSignLoad(true);
      await firebaseState.doSignInWithEmailAndPassword(email, password);
    } catch (error) {
      setSignLoad(false);
      setSignError(t("badCredentials"));
      console.error(error);
    }
  };

  const onSubmitGoogle = async () => {
    await firebaseState.signInWithGooglePopUp();
  };

  const forgotPassClick = () => {
    const projectId = firebaseState.firebase.app().options.projectId;
    window.open(constants.ENV_URLS[projectId] + "/passwordreset", "_blank");
  };
  const handleIconClick = (type) => {
    type === "password" ? setTextType("text") : setTextType("password");
  };
  return (
    <>
      <style>{classStyles}</style>
      <form
        style={{ width: tabletView ? "80%" : "100%" }}
        onSubmit={handleSubmit(onSubmit)}>
        <Box sx={container}>
          <Box sx={{ ...container, width: "100%" }}>
            <TextField
              sx={inputStyle}
              placeholder={t("email")}
              id={emailId}
              {...linkingOptions}
              {...register("email", {
                required: t("fieldRequired"),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t("badEmail"),
                },
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={startIconStyle}>
                    <EmailIcon
                      sx={{
                        width: mobileView || tabletView ? "1rem" : "1.25rem",
                        color: text.primary,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              type="email"
            />
            {errors.email && (
              <Typography sx={fontStyle} variant="error">
                {errors.email.message}
              </Typography>
            )}
          </Box>
          <Box sx={{ ...container, width: "100%" }}>
            <TextField
              sx={inputStyle}
              placeholder={t("password")}
              id={passwordId}
              {...register("password", {
                required: t("fieldRequired"),
                minLength: {
                  value: 5,
                  message: t("shortPassword"),
                },
              })}
              type={textType}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={startIconStyle}>
                    <LockIcon sx={adornmentStyle} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ margin: "1rem" }}>
                    <IconButton onClick={() => handleIconClick(textType)}>
                      {textType === "password" ? (
                        <VisibilityIcon sx={adornmentStyle} />
                      ) : (
                        <VisibilityOffIcon sx={adornmentStyle} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errors.password && (
              <Typography sx={fontStyle} variant="error">
                {errors.password.message}
              </Typography>
            )}
            {signError && (
              <Typography id={signErrorId} sx={fontStyle} variant="error">
                {signError}
              </Typography>
            )}
          </Box>
          <Box sx={buttonContainer}>
            <Button sx={logButton} variant="contained" type="submit">
              {t("signIn")}
            </Button>
            <Button
              sx={googleButton}
              color="info"
              variant="outlined"
              onClick={onSubmitGoogle}>
              {t("signInGoogle")}
            </Button>
            <Button sx={passButton} onClick={forgotPassClick}>
              {t("forgotPassword")}
            </Button>
            <a
              href="https://saferoadschallenge.com/terms-of-use/"
              style={tinyBtnStyle}>
              {t("tAndC")}
            </a>
            <a
              href="https://saferoadschallenge.com/privacy-policy/"
              style={tinyBtnStyle}>
              {t("privacyPolicy")}
            </a>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default SignForm;
